import { useState, useEffect, useCallback } from "react";
import HelpButton from "../components/HelpButton";
import axios from "axios";
import api from "../lib/api";
import { API } from "../App";
import Sidebar from "../components/Sidebar";
import { toast } from "sonner";
import { Plus, X, PencilSimple, Trash, ShieldCheck, Lock, Check } from "@phosphor-icons/react";
import PermissionMatrixTab from "../components/admin/PermissionMatrixTab";

const LEGACY_ROLE_KEYS = ["admin", "cfo", "marketing_head", "manager", "bdm", "bde", "dealer", "customer"];

const empty = {
  name: "", code: "", department_id: "", department_name: "",
  reporting_role_id: "", reporting_role_name: "",
  legacy_role_key: "bde", permission_group: "", description: "", status: "active",
};

const AdminRoles = ({ user, logout }) => {
  const [activeTab, setActiveTab] = useState("org_roles"); // "org_roles" | "perm_matrix"
  const [items, setItems] = useState([]);
  const [depts, setDepts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [permsCatalog, setPermsCatalog] = useState({ catalog: {}, all_keys: [], defaults_by_legacy_key: {} });
  const [permsRole, setPermsRole] = useState(null);  // role whose permissions we're editing
  const [permsSelected, setPermsSelected] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, d, p] = await Promise.all([
        axios.get(`${API}/org-roles`),
        axios.get(`${API}/departments`),
        api.get(`${API}/permissions/catalog`).catch(() => ({ data: { catalog: {}, all_keys: [], defaults_by_legacy_key: {} } })),
      ]);
      setItems(r.data || []);
      setDepts(d.data || []);
      setPermsCatalog(p.data || { catalog: {}, all_keys: [], defaults_by_legacy_key: {} });
    } catch (e) { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await axios.put(`${API}/org-roles/${editing.role_id}?requester_role=${user?.role || ""}`, form);
        toast.success("Role updated");
      } else {
        await axios.post(`${API}/org-roles?requester_role=${user?.role || ""}`, form);
        toast.success("Role created");
      }
      setShowForm(false); setEditing(null); setForm(empty);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const startEdit = (r) => { setEditing(r); setForm({ ...empty, ...r }); setShowForm(true); };

  const remove = async (r) => {
    if (!window.confirm(`Delete role "${r.name}"?`)) return;
    try {
      await api.delete(`/org-roles/${r.role_id}`);
      toast.success("Deleted");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const openPerms = (r) => {
    setPermsRole(r);
    const fallback = permsCatalog.defaults_by_legacy_key?.[r.legacy_role_key] || [];
    setPermsSelected(r.permissions && r.permissions.length ? r.permissions : fallback);
  };

  const togglePerm = (key) => {
    setPermsSelected(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const savePerms = async () => {
    try {
      await api.put(`/org-roles/${permsRole.role_id}/permissions`, {
        permissions: permsSelected,
      });
      toast.success("Permissions updated");
      setPermsRole(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const resetPermsToDefault = () => {
    const defaults = permsCatalog.defaults_by_legacy_key?.[permsRole.legacy_role_key] || [];
    setPermsSelected(defaults);
  };

  const inputCls = "rounded-none bg-white border border-slate-300 focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] p-2.5 w-full outline-none text-sm";
  const labelCls = "text-[10px] uppercase tracking-wider font-bold text-slate-500 block mb-1";

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar user={user} logout={logout} />
      <div className="flex-1 ml-64">
        <div className="p-8">
          <div className="flex justify-between items-start mb-8 flex-wrap gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-1">Org Structure</div>
              <div className="flex items-center"><h1 className="text-4xl sm:text-5xl tracking-tighter leading-none font-bold mb-2" style={{fontFamily: 'Outfit, sans-serif'}}>
                Roles
              </h1><HelpButton moduleKey="admin_settings" /></div>
              <p className="text-base leading-relaxed text-slate-700">{items.length} organisation roles · mapped to {depts.length} departments</p>
            </div>
            {activeTab === "org_roles" && (
            <button
              data-testid="role-add-btn"
              onClick={() => { setEditing(null); setForm(empty); setShowForm(true); }}
              className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-5 py-2.5 font-medium inline-flex items-center gap-2"
            >
              <Plus size={16} weight="bold" /> Add Role
            </button>
            )}
          </div>

          {/* Phase B.4 — Tab switcher */}
          <div data-testid="admin-roles-tabs" className="flex border-b border-slate-200 mb-6">
            <button
              data-testid="tab-org-roles"
              onClick={() => setActiveTab("org_roles")}
              className={`px-5 py-3 text-sm font-bold uppercase tracking-wider border-b-2 transition-colors ${
                activeTab === "org_roles"
                  ? "border-[#002FA7] text-[#002FA7]"
                  : "border-transparent text-slate-500 hover:text-slate-800"
              }`}
            >
              Org Roles
            </button>
            <button
              data-testid="tab-perm-matrix"
              onClick={() => setActiveTab("perm_matrix")}
              className={`px-5 py-3 text-sm font-bold uppercase tracking-wider border-b-2 transition-colors ${
                activeTab === "perm_matrix"
                  ? "border-[#002FA7] text-[#002FA7]"
                  : "border-transparent text-slate-500 hover:text-slate-800"
              }`}
            >
              Permission Matrix
            </button>
          </div>

          {activeTab === "perm_matrix" ? (
            <PermissionMatrixTab user={user} />
          ) : (<>

          {showForm && (
            <div data-testid="role-form" className="bg-white border border-slate-200 p-6 mb-8 shadow-sm">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold" style={{fontFamily: 'Outfit, sans-serif'}}>{editing ? "Edit Role" : "New Role"}</h3>
                <button onClick={() => { setShowForm(false); setEditing(null); }} className="p-1 hover:bg-slate-100"><X size={18} /></button>
              </div>
              <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><label className={labelCls}>Role Name *</label><input data-testid="role-name" required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className={inputCls} /></div>
                <div><label className={labelCls}>Code</label><input data-testid="role-code" value={form.code} onChange={(e) => setForm({...form, code: e.target.value.toLowerCase().replace(/\s+/g, "_")})} className={inputCls} placeholder="service_coordinator" /></div>
                <div>
                  <label className={labelCls}>Department *</label>
                  <select data-testid="role-dept" required value={form.department_id} onChange={(e) => {
                    const d = depts.find(x => x.department_id === e.target.value);
                    setForm({...form, department_id: e.target.value, department_name: d?.name || ""});
                  }} className={inputCls}>
                    <option value="">Select department</option>
                    {depts.map(d => <option key={d.department_id} value={d.department_id}>{d.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Reports to (role)</label>
                  <select data-testid="role-reports-to" value={form.reporting_role_id} onChange={(e) => {
                    const r = items.find(x => x.role_id === e.target.value);
                    setForm({...form, reporting_role_id: e.target.value, reporting_role_name: r?.name || ""});
                  }} className={inputCls}>
                    <option value="">None</option>
                    {items.filter(r => r.role_id !== editing?.role_id).map(r => <option key={r.role_id} value={r.role_id}>{r.name} ({r.department_name})</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Auth Permission Tier *</label>
                  <select data-testid="role-legacy-key" value={form.legacy_role_key} onChange={(e) => setForm({...form, legacy_role_key: e.target.value})} className={inputCls}>
                    {LEGACY_ROLE_KEYS.map(k => <option key={k} value={k}>{k}</option>)}
                  </select>
                  <div className="text-[10px] text-slate-500 mt-1">Drives login permissions (admin = full access, bde = limited)</div>
                </div>
                <div>
                  <label className={labelCls}>Permission Group</label>
                  <input data-testid="role-perm-group" value={form.permission_group} onChange={(e) => setForm({...form, permission_group: e.target.value})} className={inputCls} placeholder="marketing_field_rep" />
                </div>
                <div>
                  <label className={labelCls}>Status</label>
                  <select data-testid="role-status" value={form.status} onChange={(e) => setForm({...form, status: e.target.value})} className={inputCls}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <div className="md:col-span-2"><label className={labelCls}>Description</label><textarea data-testid="role-desc" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className={inputCls} rows="2" /></div>
                <div className="md:col-span-2"><button data-testid="role-save-btn" type="submit" className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-6 py-2.5 font-medium">Save</button></div>
              </form>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-20"><div className="loading-spinner"></div></div>
          ) : (
            <div data-testid="role-table" className="bg-white border border-slate-200 shadow-sm overflow-x-auto">
              <table className="w-full min-w-[900px]">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left p-3 text-xs uppercase tracking-wider font-bold text-slate-500">Role</th>
                    <th className="text-left p-3 text-xs uppercase tracking-wider font-bold text-slate-500">Department</th>
                    <th className="text-left p-3 text-xs uppercase tracking-wider font-bold text-slate-500">Reports To</th>
                    <th className="text-left p-3 text-xs uppercase tracking-wider font-bold text-slate-500">Auth Tier</th>
                    <th className="text-left p-3 text-xs uppercase tracking-wider font-bold text-slate-500">Status</th>
                    <th className="text-right p-3 text-xs uppercase tracking-wider font-bold text-slate-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((r, i) => (
                    <tr key={r.role_id} data-testid={`role-row-${i}`} className="border-b border-slate-100 hover:bg-slate-50/50">
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <ShieldCheck size={14} className="text-[#002FA7]" />
                          <div>
                            <div className="font-semibold text-slate-900">{r.name}</div>
                            {r.code && <div className="text-[10px] uppercase tracking-wider text-slate-400 font-mono">{r.code}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="p-3 text-sm text-slate-700">{r.department_name || "—"}</td>
                      <td className="p-3 text-sm text-slate-600">{r.reporting_role_name || <span className="text-slate-400">—</span>}</td>
                      <td className="p-3"><span className="text-[10px] uppercase tracking-wider font-bold bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5">{r.legacy_role_key}</span></td>
                      <td className="p-3"><span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 border ${r.status === "active" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-500 border-slate-200"}`}>{r.status}</span></td>
                      <td className="p-3 text-right">
                        <button data-testid={`role-perms-${i}`} onClick={() => openPerms(r)} className="p-1.5 text-[#002FA7] hover:bg-blue-50 mr-1" title="Permissions">
                          <Lock size={14} weight="bold" />
                        </button>
                        <button data-testid={`role-edit-${i}`} onClick={() => startEdit(r)} className="p-1.5 text-slate-700 hover:bg-slate-100"><PencilSimple size={14} /></button>
                        <button data-testid={`role-delete-${i}`} onClick={() => remove(r)} className="p-1.5 text-rose-700 hover:bg-rose-50 ml-1"><Trash size={14} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          </>)}
        </div>
      </div>

      {permsRole && (
        <div className="fixed inset-0 z-50 bg-black/40 flex justify-end">
          <div data-testid="perms-drawer" className="bg-white w-full max-w-2xl h-full overflow-y-auto border-l border-slate-200 shadow-2xl">
            <div className="sticky top-0 bg-white border-b border-slate-200 p-5 flex items-center justify-between z-10">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-slate-400">Permissions</div>
                <h3 className="text-xl font-semibold" style={{fontFamily: 'Outfit, sans-serif'}}>{permsRole.name}</h3>
                <div className="text-xs text-slate-500">{permsRole.department_name} · auth tier <span className="font-semibold text-blue-700">{permsRole.legacy_role_key}</span></div>
              </div>
              <button data-testid="perms-close" onClick={() => setPermsRole(null)} className="p-2 hover:bg-slate-100"><X size={20} /></button>
            </div>
            <div className="p-5 space-y-5">
              <div className="flex items-center justify-between bg-slate-50 border border-slate-200 p-3">
                <div className="text-sm text-slate-700">
                  <span className="font-semibold">{permsSelected.length}</span> of <span>{permsCatalog.all_keys?.length || 0}</span> permissions enabled
                </div>
                <button data-testid="perms-reset-btn" onClick={resetPermsToDefault} className="text-xs uppercase tracking-wider font-bold text-slate-700 hover:text-[#002FA7]">
                  Reset to legacy defaults
                </button>
              </div>

              {Object.entries(permsCatalog.catalog || {}).map(([groupKey, items]) => (
                <div key={groupKey} data-testid={`perms-group-${groupKey}`}>
                  <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-2 border-b border-slate-200 pb-1">{groupKey}</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                    {items.map(p => {
                      const checked = permsSelected.includes(p.key);
                      return (
                        <label key={p.key} data-testid={`perm-${p.key}`} className={`flex items-start gap-2 p-2 cursor-pointer transition-colors ${checked ? "bg-blue-50/60" : "hover:bg-slate-50"}`}>
                          <div className={`mt-0.5 w-4 h-4 border flex items-center justify-center shrink-0 ${checked ? "bg-[#002FA7] border-[#002FA7]" : "border-slate-300"}`}>
                            {checked && <Check size={10} weight="bold" className="text-white" />}
                          </div>
                          <input type="checkbox" className="hidden" checked={checked} onChange={() => togglePerm(p.key)} />
                          <div>
                            <div className="text-sm text-slate-800">{p.label}</div>
                            <div className="text-[10px] text-slate-400 font-mono">{p.key}</div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}

              <button data-testid="perms-save-btn" onClick={savePerms} className="w-full rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-5 py-3 font-medium transition-colors">
                Save Permissions
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminRoles;
